import React, { useState, useCallback } from "react";
import { PlayerOverview } from "@components/overview/PlayerOverview";
import { MainLayout } from "@components/layouts/MainLayout";
import { PageHeader } from "@components/layouts/PageHeader";
import { LayoutDashboard } from "lucide-react";
import { Separator } from "@components/ui/separator";
import { useApiDataWithDateRange } from "@hooks/useApiDataWithDateRange";

export default function Overview() {
  // Date range state
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 86400000), // 1 day ago
    to: new Date(),
  });
  const [activeRange, setActiveRange] = useState<"1d" | "7d" | "30d">("1d");

  // Button handlers
  const setLastDay = useCallback(() => {
    setDateRange({
      from: new Date(Date.now() - 86400000),
      to: new Date(),
    });
    setActiveRange("1d");
  }, []);
  const setLastWeek = useCallback(() => {
    setDateRange({
      from: new Date(Date.now() - 86400000 * 7),
      to: new Date(),
    });
    setActiveRange("7d");
  }, []);
  const setLastMonth = useCallback(() => {
    setDateRange({
      from: new Date(Date.now() - 86400000 * 30),
      to: new Date(),
    });
    setActiveRange("30d");
  }, []);

  // Match Statistics.tsx: always return an array of players
  const [allPlayerStats, statsLoading, statsError] = useApiDataWithDateRange(
    "/api/superone/all-player-stats",
    dateRange,
    {
      transform: (res: any) => {
        return res?.data?.players ?? res?.data ?? [];
      },
    }
  );
  const [activity, activityLoading, activityError] = useApiDataWithDateRange(
    "/api/superone/activity-stats-7-days-with-club",
    dateRange,
    { transform: (res: any) => res?.activityStats ?? res }
  );
  const loading = statsLoading || activityLoading;
  const error = statsError || activityError;

  return (
    <MainLayout>
      <div className="space-y-6 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2 relative">
          <PageHeader
            icon={<LayoutDashboard className="h-6 w-6 text-blue-600" />}
            title="Overview"
            subtitle="Key player and platform metrics at a glance."
          />
          {/* Date range buttons in top right */}
          <div className="flex gap-2 absolute right-0 top-0 md:static md:ml-auto">
            <button
              className={`px-4 py-2 rounded ${
                activeRange === "1d"
                  ? "bg-blue-600 text-white px-4 py-2 rounded cursor-pointer font-normal"
                  : "bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
              }`}
              onClick={setLastDay}
            >
              1 Day
            </button>
            <button
              className={`px-4 py-2 rounded ${
                activeRange === "7d"
                  ? "bg-blue-600 text-white px-4 py-2 rounded cursor-pointer font-normal"
                  : "bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
              }`}
              onClick={setLastWeek}
            >
              7 Days
            </button>
            <button
              className={`px-4 py-2 rounded ${
                activeRange === "30d"
                  ? "bg-blue-600 text-white px-4 py-2 rounded cursor-pointer font-normal"
                  : "bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
              }`}
              onClick={setLastMonth}
            >
              30 Days
            </button>
          </div>
        </div>
        <Separator className="mb-6" />
        <PlayerOverview
          stats={allPlayerStats}
          activity={activity}
          loading={loading}
          error={error}
          dateRange={dateRange}
          startDate={dateRange?.from}
          endDate={dateRange?.to}
        />
      </div>
    </MainLayout>
  );
}
